import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { LogOut, Info, Eye, Wrench, ArrowLeftRight, Send, Presentation, Briefcase } from "lucide-react";
import { useConsoleMode, MODE_CLIENT, MODE_OPERATOR } from "./useConsoleMode";

/**
 * ConsoleShell — shared layout for all console pages.
 * Light, soothing theme. Optional left sidebar slot.
 */
export default function ConsoleShell({ breadcrumb, sidebar, hideIllustrativeRibbon = false, liveEngagement = false, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const isBd = (user?.role || "").toLowerCase() === "bd";
  const canOutreach = isAdmin || isBd || !!user?.outreach_access;
  const canDemo = isAdmin || isBd;
  const [mode, setMode] = useConsoleMode();
  const isClient = mode === MODE_CLIENT;
  // Reviewer role is view-only — pin them to Client Workspace mode so the
  // operator surfaces (Signal Intake extract panel, delete controls, KOL
  // regenerate button, etc.) never render for them. Also hides the mode
  // toggle button. Server-side `require_writer` still enforces write
  // restrictions regardless of what the UI shows.
  const isReviewer = (user?.role || "").toLowerCase() === "reviewer" || isBd;
  const onOutreach = location.pathname.startsWith("/outreach") || location.pathname.startsWith("/demo");
  useEffect(() => {
    if (isReviewer && mode !== MODE_CLIENT && !onOutreach) {
      setMode(MODE_CLIENT);
    }
  }, [isReviewer, mode, setMode, onOutreach]);
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(116);

  // Override global dark body bg while a console page is mounted, so over-scroll
  // / rubber-band never reveals the public-site dark background.
  useEffect(() => {
    const prevBody = document.body.style.backgroundColor;
    const prevHtml = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = "#FAF7F1";
    document.documentElement.style.backgroundColor = "#FAF7F1";
    return () => {
      document.body.style.backgroundColor = prevBody;
      document.documentElement.style.backgroundColor = prevHtml;
    };
  }, []);

  // Measure header height so the left sidebar can stick directly below it
  // regardless of mode (the services-engagement ribbon only renders in client mode,
  // so the header height changes when the mode toggle is flipped).
  useEffect(() => {
    if (!headerRef.current) return;
    const measure = () => setHeaderH(headerRef.current?.offsetHeight || 116);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headerRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isClient]);

  const onLogout = async () => {
    await logout();
    nav("/console/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-[#0F1825]" data-testid="console-shell">
      {/* Top bar */}
      <header ref={headerRef} className="bg-white border-b border-[#E8E1D2] sticky top-0 z-40">
        <div className="px-6 py-3 flex items-center justify-between gap-6">
          <div className="flex items-center gap-6 min-w-0">
            <Link to={isBd ? "/outreach" : "/console/engagements"} className="flex items-center gap-3 shrink-0 group" data-testid="console-logo-link">
              <img
                src="/assets/logo/v2e-logo-light.png"
                alt="Voice2Evidence"
                className="h-7 w-auto group-hover:opacity-80 transition-opacity"
              />
              <div className="hidden md:flex h-7 w-px bg-[#E8E1D2]" />
              <span className="hidden md:inline font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">
                {isClient ? "Voice2Evidence Client Workspace" : "Operator Console"}
              </span>
            </Link>
            <div className="hidden md:block h-7 w-px bg-[#E8E1D2]" />
            <nav className="hidden md:flex items-center gap-1.5 text-[12.5px] text-[#5C6470] min-w-0 truncate" data-testid="breadcrumb">
              {breadcrumb}
            </nav>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {canOutreach && (isReviewer || !isClient) && (
              <Link
                to="/outreach"
                className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1 transition-colors ${
                  location.pathname.startsWith("/outreach")
                    ? "text-white bg-[#C84C21] border-[#C84C21]"
                    : "text-[#0F1825] bg-white border-[#E8E1D2] hover:border-[#C84C21] hover:text-[#C84C21]"
                }`}
                data-testid="nav-outreach"
                title="Prospect Outreach"
              >
                <Send size={11} />
                <span className="hidden md:inline">Prospect Outreach</span>
              </Link>
            )}
            {canDemo && (isReviewer || !isClient) && (
              <Link
                to="/demo"
                className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1 transition-colors ${
                  location.pathname.startsWith("/demo") || location.search.includes("demo=1")
                    ? "text-white bg-[#0F1825] border-[#0F1825]"
                    : "text-[#0F1825] bg-white border-[#E8E1D2] hover:border-[#0F1825]"
                }`}
                data-testid="nav-demo"
                title="Guided client demo"
              >
                <Presentation size={11} />
                <span className="hidden md:inline">Client demo</span>
              </Link>
            )}
            {/* Mode toggle — replaces the previous static badge. Hidden
                for reviewers who are hard-pinned to Client Workspace. */}
            {!isReviewer && (
            <button
              type="button"
              onClick={() => setMode(isClient ? MODE_OPERATOR : MODE_CLIENT)}
              className={`group hidden md:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1 transition-colors ${
                isClient
                  ? "text-[#9A6B1A] bg-[#FDF7E6] border-[#E8D08A] hover:border-[#9A6B1A]"
                  : "text-[#0F1825] bg-white border-[#0F1825] hover:border-[#C84C21] hover:text-[#C84C21]"
              }`}
              data-testid="mode-toggle"
              title={isClient ? "Switch to Operator View" : "Switch to Client Briefing View"}
            >
              {isClient ? <Eye size={11} /> : <Wrench size={11} />}
              <span data-testid="mode-label">{isClient ? "Client Workspace" : "Operator View"}</span>
              <ArrowLeftRight size={10} className="opacity-50 group-hover:opacity-100 ml-1" />
            </button>
            )}
            {isReviewer && (
              <span
                className={`hidden md:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1 ${isBd ? "text-[#2E4A38] bg-[#EAF3EC] border-[#3F6D55]/40" : "text-[#4E378A] bg-[#EDE9F4] border-[#C9BFE0]"}`}
                data-testid={isBd ? "bd-badge" : "reviewer-badge"}
              >
                {isBd ? <Briefcase size={11} /> : <Eye size={11} />}
                {isBd ? "Business Development" : "Reviewer · Read-only"}
              </span>
            )}
            <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.16em] text-[#5C6470]" data-testid="console-user">
              {user?.name || user?.email}
            </span>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#5C6470] hover:text-[#C84C21] transition-colors"
              data-testid="console-logout"
            >
              <LogOut size={14} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
        {/* Illustrative-engagement disclosure ribbon — hidden for live client engagements */}
        {!hideIllustrativeRibbon && (
          <div
            className="bg-[#F5EFE0] border-t border-[#E8DCBE] px-6 py-2 flex items-center gap-3"
            data-testid="illustrative-ribbon"
          >
            <Info size={13} className="text-[#9A6B1A] shrink-0" />
            <div className="text-[11.5px] leading-snug text-[#5C4520]">
              <span className="font-mono uppercase tracking-[0.22em] text-[#9A6B1A] text-[10px] mr-2">Illustrative engagement</span>
              <span className="text-[#5C4520]">
                This simulated case study is for demonstration only. It is not based on a live engagement, client data, confidential information, or proprietary information from the asset owner. Publicly available references may be used only to demonstrate workflow logic.
              </span>
            </div>
          </div>
        )}
        {/* Live client engagement ribbon — replaces the illustrative banner on real client work */}
        {liveEngagement && (
          <div
            className="bg-[#EAF3EC] border-t border-[#CFE2D2] px-6 py-2 flex items-center gap-3"
            data-testid="live-engagement-ribbon"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3F6D55] shrink-0" />
            <div className="text-[11.5px] leading-snug text-[#2E4A38]">
              <span className="font-mono uppercase tracking-[0.22em] text-[#3F6D55] text-[10px] mr-2">Live client engagement</span>
              <span className="text-[#2E4A38]">
                Active Voice2Evidence engagement. This workspace contains client-shared decisions, scope, and methodologist outputs. Treat all content as confidential client material.
              </span>
            </div>
          </div>
        )}
        {/* Services-engagement note — persistent across the workspace.
            Distinguishes this from a self-serve software subscription so
            client teams understand what they're looking at. */}
        {isClient && (
          <div
            className="bg-white border-t border-[#E8E1D2] px-6 py-2 flex items-center gap-3"
            data-testid="services-engagement-ribbon"
          >
            <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#C84C21] shrink-0">Services engagement</span>
            <div className="text-[11.5px] leading-snug text-[#5C6470]">
              This workspace is operated by the Voice2Evidence team as part of a services engagement. It is not a self-serve software subscription.
            </div>
          </div>
        )}
      </header>

      {/* Body */}
      <div className="flex items-start">
        {sidebar && (
          <aside
            className="hidden lg:block w-72 shrink-0 border-r border-[#E8E1D2] bg-white sticky overflow-y-auto"
            style={{ top: headerH, height: `calc(100vh - ${headerH}px)` }}
            data-testid="ws-sidebar-aside"
          >
            {sidebar}
          </aside>
        )}
        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}

export function Breadcrumb({ items }) {
  return (
    <>
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5 min-w-0">
          {i > 0 && <span className="text-[#C4B89E]">/</span>}
          {it.to ? (
            <Link to={it.to} className="hover:text-[#C84C21] transition-colors truncate">{it.label}</Link>
          ) : (
            <span className={`truncate ${i === items.length - 1 ? "text-[#0F1825] font-medium" : ""}`}>{it.label}</span>
          )}
        </span>
      ))}
    </>
  );
}
